import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Plus } from '@phosphor-icons/react';
import { can } from '@mybusinessmate/schema';
import { BusinessHeader } from '../../../../../../components/businesses/business-header';
import { BusinessBreadcrumb, BusinessTabs } from '../../../../../../components/businesses/business-tabs';
import { StaffVault } from '../../../../../../components/deliverables/staff-vault';
import { Icon } from '../../../../../../components/Icon';
import { PageSkeleton } from '../../../../../../components/ui/skeleton';
import { LoadFailure } from '../../../../../../components/ui/state-views';
import { pillClass } from '../../../../../../components/ui/toolbar-controls';
import { staffVaultQuery } from '../../../../../../lib/deliverables-api';
import { businessQuery } from '../../../../../../lib/directory-api';
import { projectListQuery } from '../../../../../../lib/projects-api';
import { useSession } from '../../../../../../lib/session';
import { isStaffRoleName } from '../../../../../../lib/staff-nav';

export const Route = createFileRoute('/subdomain/admin/_staff/businesses/$businessId/deliverables')({
  // The vault is a list on the same 1200 canvas as the business's other tabs.
  staticData: { wide: true },
  component: BusinessDeliverablesPage,
});

/** A business's Deliverables tab (Figma A05.11): the account vault the team keeps for the client. */
function BusinessDeliverablesPage() {
  const { businessId } = Route.useParams();
  const { data: session } = useSession();
  const role = session?.user.role;
  const business = useQuery(businessQuery(businessId));
  const vault = useQuery(staffVaultQuery(businessId));
  // The "Link to" choices and the Project filter: this business's projects, every bucket that is still live.
  const active = useInfiniteQuery(projectListQuery({ businessId, bucket: 'active' }));
  const [addRequest, setAddRequest] = useState(0);

  if (business.isLoading) return <PageSkeleton />;
  if (!business.data) {
    return (
      <LoadFailure
        error={business.error}
        noun="business"
        forbiddenHint="Ask an admin to assign it to you."
        onRetry={() => void business.refetch()}
      />
    );
  }
  // Seeing the tab needs the vault answering; a refusal reads as no access rather than an empty vault.
  if ((vault.error as { kind?: string } | null)?.kind === 'forbidden') {
    return (
      <>
        <BusinessBreadcrumb businessName={business.data.name} />
        <LoadFailure
          error={vault.error}
          noun="vault"
          forbiddenHint="Only the team on this business, its account manager and admins can open it."
          onRetry={() => void vault.refetch()}
        />
      </>
    );
  }

  const staff = isStaffRoleName(role) ? role : undefined;
  // Someone who can open the list is on the business already: leads and specialists through a task, so `assigned` holds.
  const resource = {
    managesBusiness: staff === 'account_manager',
    assigned: staff === 'lead_specialist' || staff === 'specialist',
  };
  const canManage = !!staff && can({ role: staff }, 'deliverable.manage', resource);
  const canDelete = !!staff && can({ role: staff }, 'deliverable.delete', resource);
  const projects = (active.data?.pages.flatMap((p) => p.items) ?? []).map((p) => ({ id: p.id, name: p.name }));

  return (
    <>
      <BusinessBreadcrumb businessName={business.data.name} />
      <BusinessHeader
        business={business.data}
        primary={
          canManage ? (
            <button type="button" onClick={() => setAddRequest((n) => n + 1)} className={pillClass('dark', 'lg')}>
              <Icon icon={Plus} size="sm" />
              Add item
            </button>
          ) : null
        }
      />
      <BusinessTabs
        businessId={businessId}
        active="deliverables"
        projectCount={business.data.counts.projects}
        deliverableCount={vault.data?.items.length ?? business.data.counts.deliverables}
      />
      <StaffVault
        businessId={businessId}
        businessName={business.data.name}
        projects={projects}
        canManage={canManage}
        canDelete={canDelete}
        addRequest={addRequest}
      />
    </>
  );
}
